import { MouseEventHandler, ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';

interface IProps {
  // 하위 엘리먼트
  children: ReactNode;

  // variant (default = contained)
  variant?: 'contained' | 'outlined' | 'disabled';

  // 가로
  width?: number;

  // 높이
  height?: number;

  // 색상
  color?: 'wakeup' | 'main' | string;

  // 사이즈 (defeault = medium)
  size?: 'small' | 'medium';

  // 버튼 타입
  type?: 'submit' | 'button';

  // 모서리 각도
  borderRadius?: number;

  // 디스플레이 유형
  display?: 'inline-block' | 'flex' | 'inline-flex' | 'block' | 'none';
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
  alignItems?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
  // 버튼 비활성화 유무
  disabled?: boolean;

  // 클릭 이벤트
  onClick?: MouseEventHandler;

  // 링크 URL (href가 있으면 <a> 태그로 렌더링)
  href?: string;
  target?: string;
  rel?: string;

  style?: CSSProperties;
}

const ButtonTag = styled.button<IProps>`
  border-radius: ${({ borderRadius }) => (borderRadius! >= 0 ? `${borderRadius}px` : '50px')};
  display: ${({ display }) => (display ? `${display}` : '')};
  justify-content: ${({ justifyContent }) => (justifyContent ? `${justifyContent}` : '')};
  align-items: ${({ alignItems }) => (alignItems ? `${alignItems}` : '')};

  // 가로
  width: 100%;
  ${({ width }) => (width ? `max-width: ${width}px;` : '')}
  ${({ height }) => (height ? `height: ${height}px;` : '')}


  // 사이즈
  ${({ size }) =>
    size === 'small'
      ? `
  padding: 7px 15px;
  font-size: 0.8rem;
  `
      : `
  padding: 15px 20px;
  font-weight: 500;
  font-size: 0.88rem;
  `}

  // 색상
  ${({ variant, theme, color }) => {
    const useColor = color === 'main' ? theme.pltoColor : color === 'wakeup' ? theme.wakeUpColor : color;

    switch (variant) {
      case 'outlined':
        return `
          color: ${useColor};
          background: white;
          border: 1px solid ${useColor};
          :hover:not(:disabled) {
            color: white;
            background: ${useColor};
          }
        `;

      case 'disabled':
        return `
          color: #000;
          background: none;
          border: 1px solid #AAAA;

          :hover:not(:disabled) {
            color: ${useColor};
            background: none;
            border: 1px solid ${useColor};
          }
        `;

      default: // contained
        return `
          color: white;
          background: ${useColor};
          border: 1px solid ${useColor};

          :hover:not(:disabled) {
            color: ${useColor};
            background: none;
          }
        `;
    }
  }}

  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LinkTag = styled.a<IProps>`
  border-radius: ${({ borderRadius }) => (borderRadius! >= 0 ? `${borderRadius}px` : '50px')};
  display: ${({ display }) => (display ? `${display}` : '')};
  justify-content: ${({ justifyContent }) => (justifyContent ? `${justifyContent}` : '')};
  align-items: ${({ alignItems }) => (alignItems ? `${alignItems}` : '')};

  // 가로
  width: 100%;
  ${({ width }) => (width ? `max-width: ${width}px;` : '')}
  ${({ height }) => (height ? `height: ${height}px;` : '')}
  text-decoration: none;

  // 사이즈
  ${({ size }) =>
    size === 'small'
      ? `
  padding: 7px 15px;
  font-size: 0.8rem;
  `
      : `
  padding: 15px 20px;
  font-weight: 500;
  font-size: 0.88rem;
  `}

  // 색상
  ${({ variant, theme, color }) => {
    const useColor = color === 'main' ? theme.pltoColor : color === 'wakeup' ? theme.wakeUpColor : color;

    switch (variant) {
      case 'outlined':
        return `
          color: ${useColor};
          background: white;
          border: 1px solid ${useColor};
          :hover {
            color: white;
            background: ${useColor};
          }
        `;

      case 'disabled':
        return `
          color: #000;
          background: none;
          border: 1px solid #AAAA;

          :hover {
            color: ${useColor};
            background: none;
            border: 1px solid ${useColor};
          }
        `;

      default: // contained
        return `
          color: white;
          background: ${useColor};
          border: 1px solid ${useColor};

          :hover {
            color: ${useColor};
            background: none;
          }
        `;
    }
  }}

  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

function Button(props: IProps) {
  // href가 있으면 <a> 태그로 렌더링
  if (props.href) {
    const { href, target, rel, ...restProps } = props;
    return (
      <LinkTag href={href} target={target} rel={rel} {...restProps}>
        {props.children}
      </LinkTag>
    );
  }
  return <ButtonTag {...props} />;
}

export default Button;
